import React, { useState } from "react";
import { Modal, Form, Row, Col, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createClient, createContact } from "../../../api/CLIENTS/clients";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { toast } from "react-toastify";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";

const CONTACT_TYPE_OPTIONS = [
  { value: "contact", label: "Contact" },
  { value: "billing", label: "Facturation" },
];

const NewClient = ({ showNewClient, setShowNewClient }) => {
  const queryClient = useQueryClient();
  const [checkReferent, setCheckReferent] = useState(false);
  const [formError, setFormError] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);

  const [formData, setFormData] = useState({
    // Client data
    nom: "",
    adresse: "",
    email: "",
    telephone: "",
    informations: "",
    alertes: "",
    parent: null,

    // Contact data
    contact_nom: "",
    contact_email: "",
    contact_telephone: "",
    contact_type: "contact",
  });

  // Mutation to create the client
  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: async (response) => {
      const clientId = response.data?.id;

      if (clientId && showContactForm) {
        const hasContactData =
          formData.contact_nom ||
          formData.contact_email ||
          formData.contact_telephone;

        if (hasContactData) {
          try {
            await createContact(clientId, {
              contact: {
                nom: formData.contact_nom,
                email: formData.contact_email,
                telephone: formData.contact_telephone,
                type: formData.contact_type,
              },
            });
          } catch (error) {
            console.error("Erreur lors de la création du contact:", error);
            toast.warning(
              "Client créé, mais une erreur est survenue lors de la création du contact."
            );
          }
        }
      }

      toast.success("Client créé avec succès !");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      handleClose();
    },
  });

  const handleSubmitForm = (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate client email
    if (formData.email && !emailRegex.test(formData.email)) {
      setFormError("L'email du client n'est pas valide.");
      return;
    }

    // Validate client phone
    if (formData.telephone && formData.telephone.length < 10) {
      setFormError("Le numéro du client doit contenir 10 chiffres.");
      return;
    }

    // Validate contact fields only if contact form is shown
    if (showContactForm) {
      // Required contact fields
      if (!formData.contact_nom) {
        setFormError("Le nom du contact est requis.");
        return;
      }
      if (!formData.contact_email) {
        setFormError("L'email du contact est requis.");
        return;
      }
      if (!emailRegex.test(formData.contact_email)) {
        setFormError("L'email du contact n'est pas valide.");
        return;
      }
      if (!formData.contact_telephone) {
        setFormError("Le numéro du contact est requis.");
        return;
      }
      if (formData.contact_telephone.length < 10) {
        setFormError("Le numéro du contact doit contenir 10 chiffres.");
        return;
      }
    }

    setFormError("");

    const clientData = {
      client: {
        nom: formData.nom,
        adresse: formData.adresse,
        email: formData.email,
        telephone: formData.telephone,
        informations: formData.informations,
        alertes: formData.alertes,
        parent: checkReferent ? formData.parent : null,
      },
    };

    createClientMutation.mutate(clientData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactTypeChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      contact_type: selectedOption ? selectedOption.value : "contact",
    }));
  };

  const handleAddContact = () => {
    setShowContactForm(true);
  };

  const handleRemoveContact = () => {
    setShowContactForm(false);
    setFormData((prev) => ({
      ...prev,
      contact_nom: "",
      contact_email: "",
      contact_telephone: "",
      contact_type: "contact",
    }));
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      adresse: "",
      email: "",
      telephone: "",
      informations: "",
      alertes: "",
      parent: null,
      contact_nom: "",
      contact_email: "",
      contact_telephone: "",
      contact_type: "contact",
    });
    setCheckReferent(false);
    setShowContactForm(false);
  };

  const handleClose = () => {
    setShowNewClient(false);
    resetForm();
    setFormError("");
    createClientMutation.reset();
  };

  return (
    <Modal show={showNewClient} onHide={handleClose} size="lg">
      <Form onSubmit={handleSubmitForm}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="modal-title text-client">Création nouveau client</h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {createClientMutation.isError && (
            <Alert variant="danger">
              Une erreur est survenue lors de la création du client.
              {createClientMutation.error?.message &&
                ` (${createClientMutation.error.message})`}
            </Alert>
          )}
          {formError && <Alert variant="danger">{formError}</Alert>}

          {/* Client information */}
          <Form.Label className="text-muted mb-3 fw-bold">
            Informations client
          </Form.Label>
          <Row className="mb-3">
            <Col>
              <Form.Control
                type="text"
                name="nom"
                placeholder="Nom *"
                value={formData.nom}
                onChange={handleInputChange}
                required
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Control
                type="text"
                name="adresse"
                placeholder="Adresse postale"
                value={formData.adresse}
                onChange={handleInputChange}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Control
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Col>
            <Col>
              <Form.Control
                type="tel"
                name="telephone"
                placeholder="Téléphone *"
                value={formData.telephone}
                onChange={handleInputChange}
                maxLength={10}
              />
            </Col>
          </Row>

          <Row className="mb-5">
            <Col>
              <Form.Label className="text-muted">Informations complémentaires</Form.Label>
              <MarkdownEditor
                value={formData.informations}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, informations: val }))
                }
                rows={3}
                placeholder="Contexte, préférences, informations utiles…"
              />
            </Col>
            <Col>
              <Form.Label className="text-muted">Alerte</Form.Label>
              <MarkdownEditor
                value={formData.alertes}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, alertes: val }))
                }
                rows={3}
                placeholder="Points d'attention, contraintes importantes…"
              />
            </Col>
          </Row>

          {/* Contact section */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Form.Label className="text-muted fw-bold mb-0">
              Informations contact
            </Form.Label>
            {!showContactForm ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddContact}
                type="button"
              >
                + Ajouter un contact
              </Button>
            ) : (
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemoveContact}
                type="button"
              >
                ✕ Supprimer le contact
              </Button>
            )}
          </div>

          {showContactForm && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Select
                    options={CONTACT_TYPE_OPTIONS}
                    value={CONTACT_TYPE_OPTIONS.find(
                      (o) => o.value === formData.contact_type
                    )}
                    onChange={handleContactTypeChange}
                    placeholder="Type de contact *"
                    isSearchable={false}
                    styles={customSelectStyles}
                  />
                </Col>
              </Row>
              <Row className="mb-5">
                <Col>
                  <Form.Control
                    type="text"
                    name="contact_nom"
                    placeholder="Nom *"
                    value={formData.contact_nom}
                    onChange={handleInputChange}
                    required
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="email"
                    name="contact_email"
                    placeholder="Email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    required
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="tel"
                    name="contact_telephone"
                    placeholder="Téléphone *"
                    value={formData.contact_telephone}
                    onChange={handleInputChange}
                    maxLength={10}
                    required
                  />
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={createClientMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={createClientMutation.isPending}
          >
            {createClientMutation.isPending ? "Création..." : "Créer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewClient;